// Stimmungsbild hinter dem Eintrag des Übungsufers (Startbild, Auftrag des Kampf-Designers): der Tannenwald am Morgen
// (tw1) mit dem Floß, ruhig, das Wasser zieht langsam. Nach public/art/fahrt/durchfahrt.json des Grafik-Leads: Ebenen
// von hinten nach vorn, jede um versatz verschoben, beim Halten mit halten.tempo; der Kern einer Ebene geht gespiegelt
// weiter, damit sie beliebig weit treiben kann. Das Floß bleibt in der Mitte, schaukelt leicht und spiegelt sich.
// Reine Darstellung; der Ordner gehört dem Grafik-Lead, hier wird nur gelesen.

type Part = [number, number, number, number];
type Durchfahrt = {
  breite: number;
  hoehe: number;
  rand: number;
  wl: number;
  tempoVorn: number;
  ebenen: { id: string; tempo: number }[];
  boote: Record<string, { x: number; y: number; w: number; h: number; lampen: [number, number][] }>;
  biome: Record<string, { orte: { id: string; bootTon: string; lichter: number }[] }>;
  halten: { tempo: number };
  zeilen: Record<string, [number, number]>;
  atlas: Record<string, { datei: string; teile: Record<string, Part> }>;
};

const BASE = './art/fahrt/';
let data: Durchfahrt | null = null;
let loading = false;
const images = new Map<string, HTMLImageElement>();

function img(file: string): HTMLImageElement | null {
  let i = images.get(file);
  if (!i) {
    i = new Image();
    i.src = BASE + file;
    images.set(file, i);
  }
  return i.complete && i.naturalWidth ? i : null;
}

function load(): void {
  if (data || loading) return;
  loading = true;
  void fetch(`${BASE}durchfahrt.json`)
    .then((r) => (r.ok ? (r.json() as Promise<Durchfahrt>) : null))
    .then((d) => (data = d))
    .catch(() => (data = null));
}

/** Das Floß, mit dem Licht der Tageszeit multipliziert (bootTon), einmal vorbereitet */
let tinted: { key: string; quer: HTMLCanvasElement; aussen: HTMLCanvasElement } | null = null;
function boat(art: string, tone: string): typeof tinted {
  const quer = img(`boot_${art}_quer.png`);
  const aussen = img(`boot_${art}_aussen.png`);
  if (!quer || !aussen) return null;
  const key = `${art}:${tone}`;
  if (tinted?.key === key) return tinted;
  const mk = (src: HTMLImageElement) => {
    const c = document.createElement('canvas');
    c.width = src.naturalWidth;
    c.height = src.naturalHeight;
    const x = c.getContext('2d')!;
    x.drawImage(src, 0, 0);
    x.globalCompositeOperation = 'multiply';
    x.fillStyle = tone;
    x.fillRect(0, 0, c.width, c.height);
    x.globalCompositeOperation = 'destination-in';
    x.drawImage(src, 0, 0);
    return c;
  };
  tinted = { key, quer: mk(quer), aussen: mk(aussen) };
  return tinted;
}

/** Eine Ebene, um versatz verschoben; Kachel k liegt bei versatz + k · 640, ungerade Kacheln gespiegelt */
function layer(ctx: CanvasRenderingContext2D, d: Durchfahrt, atlas: HTMLImageElement, part: Part, versatz: number, rows: [number, number]): void {
  const [px, py] = part;
  const core = d.breite;
  const [top, bottom] = rows;
  const h = bottom - top;
  if (h <= 0) return;
  const k0 = Math.floor(-versatz / core) - 1;
  for (let k = k0; k <= k0 + 2; k++) {
    const x = Math.round(versatz + k * core);
    if (x > d.breite || x + core < 0) continue;
    const odd = ((k % 2) + 2) % 2 === 1;
    if (!odd) ctx.drawImage(atlas, px + d.rand, py + top, core, h, x, top, core, h);
    else {
      ctx.save();
      ctx.translate(x + core, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(atlas, px + d.rand, py + top, core, h, 0, top, core, h);
      ctx.restore();
    }
  }
}

/** Das Bild zur Zeit t (Sekunden) auf die Leinwand; false, solange die Bilder noch laden */
export function drawMood(canvas: HTMLCanvasElement, t: number, place = 'tw1', boatArt = 'floss'): boolean {
  load();
  const d = data;
  const at = d?.atlas[place];
  const atlas = at ? img(at.datei) : null;
  if (!d || !at || !atlas) return false;
  if (canvas.width !== d.breite || canvas.height !== d.hoehe) {
    canvas.width = d.breite;
    canvas.height = d.hoehe;
  }
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  const ort = Object.values(d.biome).flatMap((b) => b.orte).find((o) => o.id === place);
  // Halten: Der Ort treibt mit halten.tempo weiter
  const drift = t * d.tempoVorn * d.halten.tempo;
  const tempo = (id: string) => d.ebenen.find((e) => e.id === id)?.tempo ?? 0;
  const himmel = at.teile[`${place}_himmel`];
  if (himmel) ctx.drawImage(atlas, himmel[0], himmel[1], himmel[2], himmel[3], 0, 0, himmel[2], himmel[3]);
  for (const id of ['ferne', 'mitte', 'ufer']) {
    const part = at.teile[`${place}_${id}`];
    const rows = d.zeilen[`${place}_${id}`] ?? [0, d.hoehe];
    if (part) layer(ctx, d, atlas, part, -drift * tempo(id), rows);
  }
  // Wasser: von oben (Tempo des Ufers) nach unten (Tempo vorn) Zeile für Zeile schneller, hier in Streifen
  const water = at.teile[`${place}_wasser`];
  if (water) {
    const rows = d.zeilen[`${place}_wasser`] ?? [d.wl - 60, d.hoehe];
    const strips = 12;
    const span = rows[1] - rows[0];
    for (let i = 0; i < strips; i++) {
      const top = Math.round(rows[0] + (span * i) / strips);
      const bottom = Math.round(rows[0] + (span * (i + 1)) / strips);
      const k = (i + 0.5) / strips;
      layer(ctx, d, atlas, water, -drift * (tempo('ufer') + (tempo('vorn') - tempo('ufer')) * k), [top, bottom]);
    }
  }
  const dinge = at.teile[`${place}_dinge`];
  const dingeRows = d.zeilen[`${place}_dinge`] ?? [0, 0];
  if (dinge && dingeRows[1] > dingeRows[0]) layer(ctx, d, atlas, dinge, -drift * tempo('dinge'), dingeRows);
  // Das Floß: bleibt in der Mitte, schaukelt leicht, spiegelt sich im Wasser
  const b = d.boote[boatArt];
  const pic = b ? boat(boatArt, ort?.bootTon ?? '#ffffff') : null;
  if (b && pic) {
    const bob = Math.round(Math.sin(t * 1.3) * 1.2);
    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.translate(b.x, d.wl * 2 - b.y + bob + 1);
    ctx.scale(1, -1);
    ctx.drawImage(pic.aussen, 0, 0);
    ctx.restore();
    ctx.drawImage(pic.quer, b.x, b.y + bob);
    // Laternen, am Morgen nur ein Hauch
    const glow = ort?.lichter ?? 0.2;
    for (const [lx, ly] of b.lampen) {
      const g = ctx.createRadialGradient(b.x + lx, b.y + ly + bob, 0, b.x + lx, b.y + ly + bob, 14);
      g.addColorStop(0, `rgba(255, 214, 140, ${0.45 * glow + 0.1})`);
      g.addColorStop(1, 'rgba(255, 214, 140, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(b.x + lx - 14, b.y + ly + bob - 14, 28, 28);
    }
  }
  const vorn = at.teile[`${place}_vorn`];
  if (vorn) layer(ctx, d, atlas, vorn, -drift * tempo('vorn'), d.zeilen[`${place}_vorn`] ?? [0, d.hoehe]);
  return true;
}
